import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ENVIRONMENT, USER, USER_REF } from '@marmon/frontend/common';
import {
	Auth,
	EmailAuthProvider,
	User as FirebaseUser,
	reauthenticateWithCredential,
	signInWithEmailAndPassword,
	signInWithPopup,
	signOut,
} from '@marmon/frontend/firebase/auth';
import { docData, Firestore } from '@marmon/frontend/firebase/firestore';
import {
	createUser,
	getUserStatus,
	User,
	UserStatus,
} from '@marmon/shared/types/user';
import { getFirebaseErrorKey, sanitize } from '@marmon/shared/utils';
import { OAuthProvider, onAuthStateChanged } from 'firebase/auth';
import {
	collection,
	CollectionReference,
	doc,
	DocumentData,
	DocumentReference,
	getDoc,
	setDoc,
	Timestamp,
	updateDoc,
} from 'firebase/firestore';
import { BehaviorSubject, skip, takeWhile } from 'rxjs';
import { LeadershipService } from './leadership.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
	firebaseUser: FirebaseUser | null = null;

	user: User | null = null;
	userRef: DocumentReference<User> | null = null;

	readonly authenticated$ = new BehaviorSubject<boolean>(false);
	private readonly user$ = inject(USER);
	private readonly userRef$ = inject(USER_REF);
	private readonly leadership = inject(LeadershipService);
	private readonly environment = inject(ENVIRONMENT);
	private readonly auth = inject(Auth);
	private readonly firestore = inject(Firestore);
	private readonly router = inject(Router);

	constructor() {
		onAuthStateChanged(this.auth, this.onAuthStateChanged.bind(this));
	}

	onAuthStateChanged(firebaseUser: FirebaseUser | null) {
		if (!firebaseUser) {
			this.reset();
			return;
		}

		this.firebaseUser = firebaseUser;
		this.determineUser(this.firebaseUser);
	}

	async determineUser(firebaseUser: FirebaseUser) {
		const userCollection = collection(
			this.firestore,
			'users',
		) as CollectionReference<User, DocumentData>;

		let userSnapshot = await getDoc(doc(userCollection, firebaseUser.uid));

		if (!userSnapshot || !userSnapshot.exists()) {
			const now = Timestamp.now();
			const user = createUser(firebaseUser, {
				createdAt: now,
				acceptedAt: now,
			});

			await setDoc(
				doc(userCollection, firebaseUser.uid),
				sanitize(user),
			).catch((error) => {
				console.error('AuthService.determineUser', error);
				throw Error('Error creating new user');
			});

			userSnapshot = await getDoc(doc(userCollection, firebaseUser.uid));
		}

		this.userRef = userSnapshot.ref as DocumentReference<
			User,
			DocumentData
		>;
		this.userRef$.next(this.userRef);

		this.listenToUserChanges();
		this.user = userSnapshot.data() as User;
		this.user$.next(this.user);

		await this.leadership.load(this.user, this.userRef);

		this.authenticated$.next(true);
	}

	listenToUserChanges() {
		if (!this.userRef) throw Error('UserRef is required for changes');
		docData<User>(this.userRef)
			.pipe(
				skip(1),
				takeWhile(() => this.authenticated$.value),
			)
			.subscribe((value) => {
				const forceOut = (code?: string) =>
					this.signOut().then(() => {
						this.router.navigate(['/auth'], {
							queryParams: { alert: code ?? undefined },
						});
					});

				if (!value) return forceOut();

				this.user = value;
				this.user$.next(this.user);

				// if an error returns from the check we need to sign out
				return this.checkUserStatus(getUserStatus(value)).catch(
					(error) => forceOut(getFirebaseErrorKey(error.code)),
				);
			});
	}

	signInWithProvider(email: string, providerId: string) {
		if (!this.environment.okta) throw Error('Okta is not configured');

		const authProvider = new OAuthProvider(providerId);
		authProvider.setCustomParameters({
			include_granted_scopes: 'false',
			prompt: 'login',
			login_hint: email,
		});

		return signInWithPopup(this.auth, authProvider).catch((error) => {
			console.error('There was an issue signing in with popup', error);
			throw error;
		});
	}

	signInWithEmailAndPassword(email: string, password: string) {
		return signInWithEmailAndPassword(this.auth, email, password).catch(
			(error) => {
				console.error(
					'There was an issue signing in with email and password',
					error,
				);
				throw error;
			},
		);
	}

	readonly checkUserStatus = (status: UserStatus | null) =>
		new Promise((resolve, reject) => {
			switch (status) {
				case 'deleted': {
					return reject({
						message: 'The account has been deleted',
						code: 'auth/invalid-credential',
					});
				}
				default:
					return resolve(null);
			}
		});

	getAccountStatus(): UserStatus | null {
		if (!this.user) return null;
		return getUserStatus(this.user);
	}

	reauthenticateWithEmailAndPassword(email: string, password: string) {
		if (!this.firebaseUser) return Promise.reject();
		const credential = EmailAuthProvider.credential(email, password);
		return reauthenticateWithCredential(this.firebaseUser, credential);
	}

	readonly signOut = () => signOut(this.auth).then(() => this.reset());

	reset() {
		this.firebaseUser = null;
		this.user = null;
		this.userRef = null;
		this.user$.next(null);
		this.userRef$.next(null);
		this.leadership.reset();
		this.authenticated$.next(false);
	}

	/**
	 * Updates the signed-in user's profile. If email is updated, then
	 * it will require another sign in.
	 *
	 * @param data the data to update the auth user with
	 */
	update(
		data: Partial<
			Pick<
				User,
				| 'email'
				| 'displayName'
				| 'photoURL'
				| 'updatedAt'
				| 'requestDeletedAt'
				| 'notifications'
			>
		>,
	): Promise<{ user: User }> {
		if (!this.userRef || !this.user || !this.firebaseUser)
			throw Error('Authenticated user could not be found');

		data.updatedAt = Timestamp.now();
		return updateDoc(this.userRef, sanitize(data)).then(() => ({
			user: this.user as User,
		}));
	}
}
